import React, { useContext, useMemo } from 'react';
import {
  Markdown,
  STUDIO_DISCLOSURE_PAGE_DEFAULT_DISCLOSURE,
  StudioPrintSettingsContext,
  StudioShimmerBlock,
  UserContext,
  useGetRequestSubjectFee,
} from 'venn-components';
import styled, { css } from 'styled-components';
import {
  assertNotNil,
  Dates,
  getFullName,
  getProxyEndDate,
  getProxyStartDate,
  getProxyTypeNoun,
  getSubjectNodesWithExcludedFees,
  hasFeesAndExcludedInvestments,
  Numbers,
  STUDIO_DEFAULT_DISCLOSURE_FONT_SIZE,
  useHasFF,
  withSuspense,
} from 'venn-utils';
import { useRecoilValue } from 'recoil';
import {
  allBlockIdsState,
  allSubjects,
  getSortedUsedProxies,
  investmentProxiesState,
  requestSpecificSubjects,
  requestSubjects,
  type StudioRequestSubject,
  type UsedProxyWithParents,
} from 'venn-state';
import { uniq } from 'lodash';
import { BlocksDataModificationsDisclosure } from './PrivatesDataModificationsDisclosure';
import { useVennDailyPrivateAssetDisclosure } from './useVennDailyPrivateAssetDisclosure';
import { GetColor, getAppTitle } from 'venn-ui-kit';
import type { Portfolio } from 'venn-api';

const calculateFee = (subject: StudioRequestSubject) =>
  subject.isFiltered
    ? subject.strategy?.id && subject.feesMapping?.[subject.strategy.id]
      ? subject.feesMapping?.[subject.strategy?.id]
      : subject.portfolio?.id && subject.feesMapping?.[subject.portfolio?.id]
    : subject.feesMapping?.[subject.id];

const useExcludedFeesDisclosure = (organizationDisplayName: string) => {
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const allSubjectsData = useRecoilValue(allSubjects);
  const filteredSubjectsRootsData = allSubjectsData.filter((s) => s.filteredSubjectIds?.length);

  // If a subject has filtered items, when fetching using requestSubjects, the root is omitted (unless it's explicitly filtered).
  // For this disclosure section we need to know the root of the filtered nodes, so we need to fetch all roots of filtered nodes separately.
  const requestSubjectList = useRecoilValue(requestSubjects(allSubjectsData));
  const filteredSubjectRootList = useRecoilValue(requestSpecificSubjects(filteredSubjectsRootsData));
  const subjectsWithExcludedFees = [...requestSubjectList, ...filteredSubjectRootList].filter((subject) =>
    hasFeesAndExcludedInvestments({ subject, displayZeroFee: getRequestSubjectFee({ subject }) !== undefined }),
  );
  const excludedNodes = getSubjectNodesWithExcludedFees(subjectsWithExcludedFees);

  if (subjectsWithExcludedFees.length === 0) {
    return undefined;
  }

  // Grouped excludedNodes by root portfolio ID
  const combinedSubjects: Record<string, StudioRequestSubject> = {};
  const combinedExcludedNodes: Record<string, Portfolio[]> = {};
  subjectsWithExcludedFees.forEach((subject, index) => {
    const id = subject.portfolio?.id;
    if (excludedNodes[index] && id) {
      combinedSubjects[id] = subject;
      combinedExcludedNodes[id] = [...(combinedExcludedNodes[id] ?? []), ...excludedNodes[index]];
    }
  });

  return `
  ### **Investments excluded from advisory fees**
  The following portfolios' investments have been excluded from the stated advisory fees. Some investments' returns are already presented net of fees, while other investments are not subject to fees.
  Please contact ${organizationDisplayName} for additional information.
  ${Object.keys(combinedSubjects)
    .map(
      (subjectId) => `
  - **${combinedSubjects[subjectId]!.portfolio!.name.trim()}** *(${Numbers.safeFormatPercentage(
    calculateFee(combinedSubjects[subjectId]!),
  )} fee)* **excludes**:
  ${uniq(combinedExcludedNodes[subjectId]!.map((node) => getFullName(node)))
    .sort()
    .join(', ')}
  `,
    )
    .join('')}
  `;
};

const INTER_FONT_DISCLOSURE_TEXT =
  'Copyright 2020 The Inter Project Authors (https://github.com/rsms/inter) This Font Software is licensed under the SIL Open Font License, Version 1.1. The URL to the license is at: https://fonts.google.com/specimen/Inter/license and an FAQ is available at: https://openfontlicense.org/open-font-license-official-text/ - 26 February 2007';

const PROXY_DISCLOSURE_TEXT = `
  ### **Proxy disclosures**
  
  This table displays each investment within this report with a proxy applied. These investments can appear directly in this report, or be included in portfolios or benchmarks that appear in this report. 
  
  The proxy range displayed in this table reflects the proxy settings configured by the user in ${getAppTitle()}, and does not reflect the analysis period used in this report. Date Ranges used for analysis are reflected on each block in this report.
`;

interface ProxyTableRow {
  key: string;
  investment: { name: string; parentSubjectNames: string | undefined } | undefined;
  rowSpan: number;
  isFirst: boolean;
  isLast: boolean;
  isOdd: boolean;
  proxyType: string;
  proxyName: string;
  proxyStart: string;
  proxyEnd: string;
}

const useProxyTableRows = (): ProxyTableRow[] | null => {
  const usedProxies = useRecoilValue(investmentProxiesState);
  const holdingProxyFF = useHasFF('holding_proxy_ff');
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');

  const rows = useMemo(() => {
    const sortedUsedProxies = getSortedUsedProxies(usedProxies);
    return Object.values(sortedUsedProxies).flatMap((proxyGroup: UsedProxyWithParents[], i): ProxyTableRow[] => {
      const isOdd = i % 2 !== 0;
      const proxy = proxyGroup[0];

      if (!proxy) {
        return [];
      }

      // EXTRAPOLATE also has the boolean flag set, disable here so we don't double-count extrapolation in the layered view
      const proxyInfos = proxy.proxyInfos.map((proxyInfo) => ({
        ...proxyInfo,
        extrapolate: proxyInfo.extrapolate && proxyInfo.proxyType !== 'EXTRAPOLATE',
      }));
      const hasLayeredView = hasLayeredProxyFF && proxyInfos.length > 0;

      const investment: ProxyTableRow['investment'] = {
        name: proxy.fundName,
        parentSubjectNames: proxy.parentSubjectNames.length ? `(${proxy.parentSubjectNames.join(', ')})` : undefined,
      };

      const rows: ProxyTableRow[] = [];

      if (holdingProxyFF && proxy.proxyVariant === 'HOLDING') {
        rows.push({
          key: proxy.fundId,
          investment,
          rowSpan: 0,
          isFirst: false,
          isLast: false,
          isOdd,
          proxyType: 'Holdings',
          proxyName: proxy.proxyName,
          proxyStart: '-',
          proxyEnd: '-',
        });
      } else if (hasLayeredView) {
        proxyInfos.forEach((proxyInfo, index) => {
          rows.push({
            key: `${proxy.fundId}-${proxyInfo.proxyType}`,
            investment: index === 0 ? investment : undefined,
            rowSpan: 0,
            isFirst: false,
            isLast: false,
            isOdd,
            proxyType: getProxyTypeNoun(proxyInfo.proxyType),
            proxyName: proxyInfo.proxyName,
            proxyStart: Dates.toDDMMMYYYY(
              proxyInfo.proxyType === 'EXTRAPOLATE'
                ? getProxyStartDate({ ...proxy, proxyType: 'EXTRAPOLATE' })
                : proxyInfo.proxyStartRange,
              proxy.proxiedFrequency,
            ),
            proxyEnd: Dates.toDDMMMYYYY(
              ['BACKFILL', 'EXTRAPOLATE'].includes(proxyInfo.proxyType)
                ? getProxyEndDate({ ...proxy, proxyType: proxyInfo.proxyType })
                : proxyInfo.proxyEndRange,
              proxy.proxiedFrequency,
            ),
          });
          if (proxyInfo.extrapolate) {
            rows.push({
              key: `${proxy.fundId}-EXTRAPOLATE`,
              investment: undefined,
              rowSpan: 0,
              isFirst: false,
              isLast: false,
              isOdd,
              proxyType: getProxyTypeNoun('EXTRAPOLATE'),
              proxyName: proxyInfo.proxyName,
              proxyStart: Dates.toDDMMMYYYY(proxy.extrapolateStartDate, proxy.proxiedFrequency),
              proxyEnd: Dates.toDDMMMYYYY(proxy.extrapolateEndDate, proxy.proxiedFrequency),
            });
          }
        });
      } else {
        rows.push({
          key: `${proxy.fundId}-${proxy.proxyType}`,
          investment,
          rowSpan: 0,
          isFirst: false,
          isLast: false,
          isOdd,
          proxyType: getProxyTypeNoun(proxy.proxyType),
          proxyName: proxy.proxyName,
          proxyStart: Dates.toDDMMMYYYY(getProxyStartDate(proxy), proxy.proxiedFrequency),
          proxyEnd: Dates.toDDMMMYYYY(getProxyEndDate(proxy), proxy.proxiedFrequency),
        });

        // Trailing extrapolate row (non-layered view)
        if (proxy.extrapolate && proxy.proxyType !== 'EXTRAPOLATE') {
          rows.push({
            key: `${proxy.fundId}-${proxy.proxyType}-extrapolate`,
            investment: undefined,
            rowSpan: 0,
            isFirst: false,
            isLast: false,
            isOdd,
            proxyType: getProxyTypeNoun('EXTRAPOLATE'),
            proxyName: proxy.proxyName,
            proxyStart: Dates.toDDMMMYYYY(
              getProxyStartDate({ ...proxy, proxyType: 'EXTRAPOLATE' }),
              proxy.proxiedFrequency,
            ),
            proxyEnd: Dates.toDDMMMYYYY(
              getProxyEndDate({ ...proxy, proxyType: 'EXTRAPOLATE' }),
              proxy.proxiedFrequency,
            ),
          });
        }
      }

      // Additional rows for holdingProxyFF multi-proxy groups
      if (holdingProxyFF && proxyGroup.length > 1) {
        proxyGroup.slice(1).forEach((additionalProxy) => {
          rows.push({
            key: `${additionalProxy.fundId}-${additionalProxy.proxyType}`,
            investment: undefined,
            rowSpan: 0,
            isFirst: false,
            isLast: false,
            isOdd,
            proxyType:
              additionalProxy.proxyVariant === 'HOLDING' ? 'Holdings' : getProxyTypeNoun(additionalProxy.proxyType),
            proxyName: additionalProxy.proxyName,
            proxyStart:
              additionalProxy.proxyVariant === 'HOLDING'
                ? '-'
                : Dates.toDDMMMYYYY(getProxyStartDate(additionalProxy), additionalProxy.proxiedFrequency),
            proxyEnd:
              additionalProxy.proxyVariant === 'HOLDING'
                ? '-'
                : Dates.toDDMMMYYYY(getProxyEndDate(additionalProxy), additionalProxy.proxiedFrequency),
          });
        });
      }

      if (rows.length > 0) {
        rows[0]!.rowSpan = rows.length;
        rows[0]!.isFirst = true;
        rows[rows.length - 1]!.isLast = true;
      }

      return rows;
    });
  }, [usedProxies, holdingProxyFF, hasLayeredProxyFF]);

  if (!rows.length) {
    return null;
  }

  return rows;
};

const ProxyDisclosures = ({ fontSize }: { fontSize: number }) => {
  const proxyTableRows = useProxyTableRows();

  if (!proxyTableRows) {
    return null;
  }

  return (
    <div data-testid="qa-proxy-disclosure">
      <StyledMarkdown noMargin fontSize={fontSize} children={PROXY_DISCLOSURE_TEXT} />
      <TableWrapper fontSize={fontSize}>
        <TableCell>
          <div>
            <b>Investment Name</b>
          </div>
          <div>(Portfolio or Benchmark)</div>
        </TableCell>
        <TableCell>
          <b>Proxy Type</b>
        </TableCell>
        <TableCell>
          <b>Proxy Name</b>
        </TableCell>
        <TableCell>
          <b>Proxy Start</b>
        </TableCell>
        <TableCell>
          <b>Proxy End</b>
        </TableCell>
        {proxyTableRows.map((proxyTableRow) => (
          <React.Fragment key={proxyTableRow.key}>
            {proxyTableRow.investment && (
              <TableCell isOdd={proxyTableRow.isOdd} rowSpan={proxyTableRow.rowSpan}>
                {proxyTableRow.investment.name && (
                  <div>
                    <b>{proxyTableRow.investment.name}</b>
                  </div>
                )}
                {proxyTableRow.investment.parentSubjectNames && (
                  <div>{proxyTableRow.investment.parentSubjectNames}</div>
                )}
              </TableCell>
            )}
            <TableCell
              isOdd={proxyTableRow.isOdd}
              topPadding={proxyTableRow.isFirst}
              bottomPadding={proxyTableRow.isLast}
            >
              {proxyTableRow.proxyType}
            </TableCell>
            <TableCell
              isOdd={proxyTableRow.isOdd}
              topPadding={proxyTableRow.isFirst}
              bottomPadding={proxyTableRow.isLast}
            >
              {proxyTableRow.proxyName}
            </TableCell>
            <TableCell
              isOdd={proxyTableRow.isOdd}
              topPadding={proxyTableRow.isFirst}
              bottomPadding={proxyTableRow.isLast}
            >
              {proxyTableRow.proxyStart}
            </TableCell>
            <TableCell
              isOdd={proxyTableRow.isOdd}
              topPadding={proxyTableRow.isFirst}
              bottomPadding={proxyTableRow.isLast}
            >
              {proxyTableRow.proxyEnd}
            </TableCell>
          </React.Fragment>
        ))}
      </TableWrapper>
    </div>
  );
};

const TableCell = styled.div<{ isOdd?: boolean; rowSpan?: number; topPadding?: boolean; bottomPadding?: boolean }>`
  padding: 0 8px;
  ${({ topPadding = true }) =>
    topPadding &&
    css`
      padding-top: 4px;
    `}
  ${({ bottomPadding = true }) =>
    bottomPadding &&
    css`
      padding-bottom: 4px;
    `}
  ${({ isOdd }) =>
    isOdd &&
    css`
      background-color: ${GetColor.GreyScale.Grey10};
    `}
  ${({ rowSpan }) =>
    rowSpan &&
    rowSpan > 1 &&
    css`
      grid-row: span ${rowSpan};
    `}
`;

const TableWrapper = styled.div<{ fontSize: number }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto auto;
  grid-template-areas:
    'header header header header header'
    'body body body body body';
  font-size: ${({ fontSize }) => fontSize}pt;
  border: 1px solid ${GetColor.GreyScale.Grey30};

  > :nth-child(-n + 5) {
    border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  margin: 8px 0;
`;

const DisclosurePage = () => {
  const { profileSettings } = useContext(UserContext);
  const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);

  const hasRcmRlCustomization = useHasFF('rcm_rl_customization_ff');

  const organizationDisplayName = assertNotNil(
    organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name,
  );

  const disclosureFontSize = organizationPrintSettings?.disclosureFontSize ?? STUDIO_DEFAULT_DISCLOSURE_FONT_SIZE;
  const baseDisclosure =
    organizationPrintSettings?.disclosure ?? STUDIO_DISCLOSURE_PAGE_DEFAULT_DISCLOSURE(organizationDisplayName);
  const disclosure = hasRcmRlCustomization ? `${baseDisclosure}${INTER_FONT_DISCLOSURE_TEXT}` : baseDisclosure;

  const excludedFeesDisclosureText = useExcludedFeesDisclosure(organizationDisplayName);
  const vdparDisclosureText = useVennDailyPrivateAssetDisclosure();

  const allBlockIds = useRecoilValue(allBlockIdsState);

  return (
    <Wrapper>
      <Header>Disclosure</Header>
      {/* eslint-disable react/no-danger */}
      {disclosure && (
        <div
          dangerouslySetInnerHTML={{
            __html: /<[a-z][\s\S]*>/i.test(disclosure)
              ? disclosure
              : disclosure
                  .split('\n\n')
                  .filter((p) => p.trim())
                  .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
                  .join(''),
          }}
          className="leading-normal text-venn-black [&_p]:m-0"
          style={{ fontSize: `${disclosureFontSize}pt` }}
        />
      )}
      {/* eslint-enable react/no-danger */}
      <OptionalSections>
        {excludedFeesDisclosureText && (
          <StyledMarkdown noMargin fontSize={disclosureFontSize} children={excludedFeesDisclosureText} />
        )}
        <BlocksDataModificationsDisclosure blockIds={allBlockIds} fontSize={disclosureFontSize} />
        {vdparDisclosureText && (
          <StyledMarkdown noMargin fontSize={disclosureFontSize} children={vdparDisclosureText} />
        )}
        <ProxyDisclosures fontSize={disclosureFontSize} />
      </OptionalSections>
      <Breaker />
    </Wrapper>
  );
};

export default withSuspense(<StudioShimmerBlock height={450} />, DisclosurePage);

const OptionalSections = styled.div`
  > div {
    border-top: 1px solid ${GetColor.GreyScale.Grey40};
    margin: 1em 0;
    page-break-inside: avoid;
  }
`;

const Breaker = styled.div`
  width: 100%;
  height: 10px;
  margin-top: -10px;
`;

const Wrapper = styled.div`
  padding: 40px 40px 0 40px;
  font-size: 10px;
  overflow: auto;

  p {
    page-break-inside: avoid;
    padding: 1em 0 0 0;
  }
`;

const Header = styled.h3`
  font-weight: bold;
`;

const StyledMarkdown = styled(Markdown)<{ fontSize: number }>`
  font-size: ${({ fontSize }) => fontSize}pt;
`;
